import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { removeTagFromContacts } from '../../api/contacts';
import { useOrganizationStore } from '../../stores/useOrganizationStore';

interface UseRemoveTagFromContactsResult {
  removeTag: (tag: string) => Promise<any>;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

/**
 * Hook for removing a tag from all contacts that have it
 * @returns Object with removeTag function, loading state, error state, and success state
 */
export const useRemoveTagFromContacts = (): UseRemoveTagFromContactsResult => {
  const { organization } = useOrganizationStore();
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: async (tag: string) => {
      if (!organization?.id) {
        throw new Error('Organization ID is required');
      }

      try {
        // Call the API and return the full response
        const result = await removeTagFromContacts(organization.id, tag);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to remove tag from contacts');
        setError(error);
        throw error;
      }
    },
  });

  const removeTag = async (tag: string) => {
    setError(null);
    // Return the full response data from the API
    const response = await mutation.mutateAsync(tag);
    return response;
  };

  return {
    removeTag,
    isLoading: mutation.isPending,
    error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};

export default useRemoveTagFromContacts;
